import React from 'react';
import { motion } from 'framer-motion';
import { Ticket, Clock, Wrench, CheckCircle } from 'lucide-react';
import { cn } from '../ui/utils';

interface TicketFiltersProps {
    currentFilter: 'all' | 'open' | 'progress' | 'completed';
    counts: { all: number; open: number; progress: number; completed: number };
    onFilterChange: (filter: 'all' | 'open' | 'progress' | 'completed') => void;
    isDarkMode: boolean;
}

export function TicketFilters({ currentFilter, counts, onFilterChange, isDarkMode }: TicketFiltersProps) {
    const filters = [
        { id: 'all', label: 'All', count: counts.all, icon: Ticket, color: 'text-slate-500' },
        { id: 'open', label: 'Open', count: counts.open, icon: Clock, color: 'text-blue-500' },
        { id: 'progress', label: 'Active', count: counts.progress, icon: Wrench, color: 'text-amber-500' },
        { id: 'completed', label: 'Done', count: counts.completed, icon: CheckCircle, color: 'text-emerald-500' },
    ] as const;

    return (
        <div className="grid grid-cols-4 gap-2">
            {filters.map((filter) => (
                <motion.button
                    key={filter.id}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onFilterChange(filter.id)}
                    className={cn(
                        "flex flex-col items-center gap-1.5 px-2 py-3 rounded-xl transition-all relative overflow-hidden",
                        currentFilter === filter.id
                            ? "bg-gradient-to-br from-rose-600 to-pink-700 text-white shadow-lg"
                            : isDarkMode
                                ? "bg-slate-800/50 text-gray-400"
                                : "bg-gray-100 text-gray-600"
                    )}
                >
                    <filter.icon className="w-5 h-5" />
                    <span className="text-xs font-medium">{filter.label}</span>
                    <span className={cn(
                        "px-2 py-0.5 rounded-full text-[10px] font-bold",
                        currentFilter === filter.id
                            ? "bg-white/20 text-white"
                            : isDarkMode
                                ? "bg-slate-700 text-gray-300"
                                : "bg-white text-gray-600"
                    )}>
                        {filter.count}
                    </span>
                </motion.button>
            ))}
        </div>
    );
}
